import { RegularSizeValue, SizeType } from "common-ts/dist/models/Cart";
import {
    BodyMeasurements,
    BodyMeasurementsForSize,
    BodyMeasurementsKey,
    CalculateClothBodyMeasurementsFromBodyMeasurements,
    CalculateClothingMeasurementsFromBodyMeasurements,
    ClothingMeasurementsKey,
    ClothingType,
    CompleteBodyMeasurements,
    FitType,
    RoundClothingMeasurementsToHalfPoint,
    UserBodyMeasurements, 
} from "common-ts/dist/models/UserMeasurements";
import { 
    inputBodyMeasurementsForm, 
    postBodyMeasurement,
    postClothingMeasurementPreference,
    resetUserMeasurementList
} from "../redux/MeasurementsRedux";
import { useAppDispatch, useAppSelector } from "../reduxhooks";
import {
    OfflineActiveScreen,
} from "../redux/UIOverlayOfflineRedux";
import PersonalSizeResult from './OverlayViewUniversalSize4Result';
import SignUp from "./OverlayViewOffline0SignUpStart";
import NameIntro from "./OverlayViewOffline0NameIntro";
import BenefitStart from "./OverlayViewOffline1BenefitStart";
import MeasureOffline from "./OverlayViewOffline1SizeSelect";
import AddressInput from "./OverlayViewOffline4AddressInput";
import PersonalSizeResultLoading from './OverlayViewUniversalSize3ResultLoading';
import ProductOption from "./OverlayViewOffline3ProductOption";
import SizeResult from "./OverlayViewOffline2SizeResult";
import SizePreset from "./OverlayViewOffline1SizePreset"; // C1
import { updateState } from "../redux/AccountRedux";
import { useEffect, useState } from "react";
import { MicrophoneState } from "../redux/MicrophoneRedux";
import { Box } from "@material-ui/core";
import OverlayViewPersonalFitModifySize from './OverlayViewPersonalSizeDetail2Edit';
import { socket } from '../socket';

// Interfaces
export interface FlowSelectionData {
    firstName: string;
    microphoneState: MicrophoneState;
}

export interface FlowStateData {
    currentScreen: OfflineActiveScreen;
    titleNumber: number;
    currentBodyMeasurements: BodyMeasurements;
    currentClothingType: ClothingType;
    currentRegularSizeValue: RegularSizeValue;
    manualOverride: boolean;
    firstIn: boolean;
}

export interface FlowStateCallback {
    delayedDispatch: (currentScreen: OfflineActiveScreen) => void;
    setTitleNumber: (titleNumber: number) => void;
    setCurrentBodyMeasurements: (updatedBodyMeasurements: BodyMeasurements) => void;
    setCurrentClothingType: (updatedClothingType: ClothingType) => void;
    setCurrentRegularSizeValue: (updatedRegularSizeValue: RegularSizeValue) => void;
    setManualOverride: (updatedManualOverride: boolean) => void;
    setFirstIn: (updatedFirstIn: boolean) => void;
    setFirstName: (firstName: string) => void;
    requestMicPermission: () => Promise<"granted" | "denied" | "error">;
    loadCartFromLocalStorage: () => void;
}

// Master component
export function FlowComponentMaster(
    flowSelectionData: FlowSelectionData,
    flowStateData: FlowStateData,
    flowStateCallback: FlowStateCallback
) {
    return FlowComponentMensTops(flowSelectionData, flowStateData, flowStateCallback);
}

// Flow component
function FlowComponentMensTops(
    flowSelectionData: FlowSelectionData,
    flowStateData: FlowStateData,
    flowStateCallback: FlowStateCallback
) {
    const dispatch = useAppDispatch();

    const delayedDispatch = flowStateCallback.delayedDispatch;
    let children: JSX.Element[] = [];

    const screenToTitleMap: Record<OfflineActiveScreen, number> = {
        [OfflineActiveScreen.StandardBrowsing]: 0,
        [OfflineActiveScreen.SignUpStart]: 0,
        [OfflineActiveScreen.BenefitStart]: 0,
        [OfflineActiveScreen.SizePreset]: 0,
        [OfflineActiveScreen.SizeSelect]: 0,
        [OfflineActiveScreen.BenefitMiddle]: 1,
        [OfflineActiveScreen.SizeLoading]: 1,
        [OfflineActiveScreen.SizeResult]: 1,
        [OfflineActiveScreen.ProductOption]: 2,
        [OfflineActiveScreen.AddressInput]: 3,
    };

    useEffect(() => {
        if (!flowStateData.manualOverride) {
            const newTitle = screenToTitleMap[flowStateData.currentScreen] ?? -1;
            if (flowStateData.titleNumber !== newTitle) {
                flowStateCallback.setTitleNumber(newTitle);
            }
        }
    }, [flowStateData.currentScreen]);

    // The ProductOption component embeds an <iframe src="/shop">. It is shown on
    // both the StandardBrowsing and ProductOption screens. To prevent the iframe
    // from refreshing when the stage changes to ProductOption (e.g. from
    // SizeResult), we render a single persistent instance below that stays
    // mounted for the whole flow and is merely hidden while other screens show.
    const showProductOption =
        flowStateData.currentScreen === OfflineActiveScreen.StandardBrowsing ||
        flowStateData.currentScreen === OfflineActiveScreen.ProductOption;

    switch (flowStateData.currentScreen) {
        case OfflineActiveScreen.StandardBrowsing:
            // Handled by the persistent ProductOption instance below.
            break;

        // case OfflineActiveScreen.SignUpStart:
        //     if (flowStateData.firstIn) {
        //         dispatch(updateState({ authState: "signedout" }));

        //         dispatch(resetUserMeasurementList());

        //         flowStateCallback.setFirstIn(false);
        //     }
        //     children.push(
        //         <SignUp
        //             handleBack={() => {
        //                 dispatch(closeOverlayAndResetWithCleanup());
        //             }}
        //             handleNext={() => {
        //                 delayedDispatch(OfflineActiveScreen.BenefitStart);
        //             }}
        //             isSignedIn={flowSelectionData.isSignedIn}
        //         />
        //     );
        //     break;

        case OfflineActiveScreen.BenefitStart:
            children.push(
                <NameIntro
                    handleNext={(firstName: string) => {
                        flowStateCallback.setFirstName(firstName);
                        delayedDispatch(OfflineActiveScreen.SizePreset); // C1
                        socket.timeout(5000).emit('chat message', {
                            currentStore: 'STORE1',
                            currentScreen: OfflineActiveScreen.SizePreset,
                            firstName: firstName,
                        });
                    }}
                />
            );
            break;

        case OfflineActiveScreen.SizePreset: // C1
            children.push(
                <SizePreset
                    handleBack={() => {
                        delayedDispatch(OfflineActiveScreen.BenefitStart);
                    }}
                    handleSelect={(measurements: BodyMeasurements, size: RegularSizeValue) => {
                        flowStateCallback.setCurrentBodyMeasurements(measurements);
                        flowStateCallback.setCurrentRegularSizeValue(size);
                        delayedDispatch(OfflineActiveScreen.SizeSelect);
                        socket.timeout(5000).emit('chat message', {
                            currentStore: 'STORE1',
                            currentScreen: OfflineActiveScreen.SizeSelect,
                            firstName: flowSelectionData.firstName,
                            currentRegularSizeValue: size,
                            currentBodyMeasurements: measurements,
                        });
                    }}
                />
            );
            break;

        case OfflineActiveScreen.SizeSelect:
            const usedMeas = flowStateData.currentBodyMeasurements ?? {};
            children.push(
            <Box style={{margin: '21px'}}>
              <OverlayViewPersonalFitModifySize
                isStoreScreen="STORE1"
                firstName={flowSelectionData.firstName}
                startingClothingBodyMeasurements={ // B-complete, CB-optional
                  CompleteBodyMeasurements(usedMeas, 
                    RegularSizeValue.L)}
                startingClothingBodyMeasurementsCBComplete={ // CB-complete
                  CalculateClothBodyMeasurementsFromBodyMeasurements(ClothingType.Body, 
                    FitType.RegularFit, 
                    usedMeas, 
                    RegularSizeValue.L, [])}
                startingClothingMeasurements={ // AUTOCOMPLETE THE CB. USE CB-COMPLETE
                  RoundClothingMeasurementsToHalfPoint(
                  CalculateClothingMeasurementsFromBodyMeasurements(ClothingType.Body, 
                    FitType.RegularFit, 
                    usedMeas, 
                    RegularSizeValue.L))}
                startingRawClothingMeasurements={ // AUTOCOMPLETE THE CB. USE CB-COMPLETE
                  CalculateClothingMeasurementsFromBodyMeasurements(ClothingType.Body, 
                    FitType.RegularFit, 
                    usedMeas, 
                    RegularSizeValue.L)}
                handleSubmit={(value, valueClothing, lastUpdatedMeasurementsList) => {
                    // 1. Update Local React State (for immediate UI needs like SizeResult)
                    flowStateCallback.setCurrentBodyMeasurements(value);

                    delayedDispatch(OfflineActiveScreen.BenefitMiddle);
                    socket.timeout(5000).emit('chat message', {
                        currentStore: 'STORE1',
                        currentScreen: OfflineActiveScreen.BenefitMiddle,
                        firstName: flowSelectionData.firstName,
                        currentRegularSizeValue: flowStateData.currentRegularSizeValue,
                        currentBodyMeasurements: value,
                    });
                }}
                isMinimized={false}
              />
            </Box>);
            // children.push(
            //     <MeasureOffline
            //         firstName={flowSelectionData.firstName}
            //         startingClothingBodyMeasurements={flowStateData.currentBodyMeasurements}
            //         startingRegularSizeValue={flowStateData.currentRegularSizeValue}
            //         microphoneState={flowSelectionData.microphoneState}
            //         requestMicPermission={flowStateCallback.requestMicPermission}
            //     />
            // );
            break;
        
        case OfflineActiveScreen.BenefitMiddle:
            children.push(
                <BenefitStart
                    handleBack={() => {
                        delayedDispatch(OfflineActiveScreen.SizeSelect);
                    }}
                    handleNext={() => {
                        delayedDispatch(OfflineActiveScreen.SizeLoading);
                        socket.timeout(5000).emit('chat message', {
                            currentStore: 'STORE1',
                            currentScreen: OfflineActiveScreen.SizeLoading,
                            firstName: flowSelectionData.firstName,
                            currentRegularSizeValue: flowStateData.currentRegularSizeValue,
                            currentBodyMeasurements: flowStateData.currentBodyMeasurements,
                        });
                    }}
                />
            );
            break;

        case OfflineActiveScreen.SizeLoading:
            children.push(<PersonalSizeResultLoading
              reductionMode={true}
              currentFlowData={{
                clothingType: ClothingType.KemejaPanjang,
                fitType: FitType.RegularFit,
                clothMeasValue: flowStateData.currentBodyMeasurements,
              }}
              onFetchMeasEstimateSuccess={() => {
                delayedDispatch(OfflineActiveScreen.SizeResult);
                socket.timeout(5000).emit('chat message', {
                    currentStore: 'STORE1',
                    currentScreen: OfflineActiveScreen.SizeResult,
                    firstName: flowSelectionData.firstName,
                    currentRegularSizeValue: flowStateData.currentRegularSizeValue,
                    currentBodyMeasurements: flowStateData.currentBodyMeasurements,
                });
              }}/>);
            break;

        case OfflineActiveScreen.SizeResult:
            const triggerList1: {[key: string]: {
              clothingMeasurementsKey: string // Same as the key for this dictionary
              bodyMeasurementsKey: string,
              triggerText: string
              triggerCallback: () => void
              showMeasurementAs?: 'SIZEWHEEL' | 'NUMBER'
            }} = {};
            triggerList1[ClothingMeasurementsKey.panjangLenganPendek as string] = {
              clothingMeasurementsKey: ClothingMeasurementsKey.panjangLenganPendek as string,
              bodyMeasurementsKey: BodyMeasurementsKey.panjangLenganPendek as string,
              triggerText: "",
              triggerCallback: () => {
              },
              showMeasurementAs: flowStateData.currentBodyMeasurements?.[BodyMeasurementsKey.panjangLenganPendek] ? 'NUMBER' : undefined,
            }
            triggerList1[ClothingMeasurementsKey.lebarLenganBaju as string] = {
              clothingMeasurementsKey: ClothingMeasurementsKey.lebarLenganBaju as string,
              bodyMeasurementsKey: BodyMeasurementsKey.lingkarLenganBisep as string,
              triggerText: "",
              triggerCallback: () => {
              },
              showMeasurementAs: flowStateData.currentBodyMeasurements?.[BodyMeasurementsKey.lebarLenganPendek] ? 'NUMBER' : undefined,
            }
            children.push(<PersonalSizeResult
              isStoreScreen="STORE1"
              name={flowSelectionData?.firstName ?? 'User'}
              bodyMeasurements={flowStateData.currentBodyMeasurements ?? {}}
              firstClothingType={ClothingType.KemejaPendek}
              firstFitType={FitType.RegularFit}
              lastUpdatedMeasurementsList={{}}
              buttonTextType={'result-detail'}
              handleBack={() => {
                delayedDispatch(OfflineActiveScreen.SizeSelect);
              }}
              handleSubmit={() => {  

                // 2. CONSTRUCT THE REAL REDUX OBJECT
                const newMeasurementId = new Date().toISOString();
                const userBodyMeasObject: UserBodyMeasurements = {
                    creationDateTime: newMeasurementId,
                    updateDateTime: newMeasurementId,
                    measurementName: "Ukuran " + flowSelectionData.firstName,
                    nama: flowSelectionData.firstName,
                    bodyMeasurements: flowStateData.currentBodyMeasurements,
                    adjustedMeasurements: flowStateData.currentBodyMeasurements, // Trust the manual/voice input
                    generatedMeasurements: flowStateData.currentBodyMeasurements,
                    sizeType: SizeType.personal,
                    bodyOrClothingMeasurements: "BODY",
                    creationMethod: "online-body", // Analytics tag
                    // Set explicitly: the draft is merged (Object.assign), so without this a
                    // leftover fitType from an earlier edit in the same session would survive.
                    fitType: 'STANDARD',
                };

                // 3. FILL THE DRAFT FORM
                dispatch(inputBodyMeasurementsForm(userBodyMeasObject));

                // 4. SAVE (Triggers LocalStorage Save & API if signed in)
                // passing '' as payload lets the thunk handle the defaults
                dispatch(postBodyMeasurement('')); 
                
                // 5. SET AS PREFERRED (Critical for the Shop to pick it up automatically)
                // We assume "BODY" is the key we want to override
                dispatch(postClothingMeasurementPreference({
                    key2: "BODY",
                    creationDateTime: "body-" + newMeasurementId
                }));

                delayedDispatch(OfflineActiveScreen.ProductOption);
              }}
              triggerList={triggerList1}
            />);
            break;

        case OfflineActiveScreen.ProductOption:
            // Handled by the persistent ProductOption instance below.
            break;

        case OfflineActiveScreen.AddressInput:
            children.push(
                <AddressInput
                    handleBack={(newTitleNumber: number) => {
                        if (newTitleNumber === 3) {
                            flowStateCallback.setManualOverride(false);
                            delayedDispatch(OfflineActiveScreen.ProductOption);
                        } else {
                            flowStateCallback.setManualOverride(true);
                            flowStateCallback.setTitleNumber(newTitleNumber);
                        }
                    }}
                    handleNext={(newTitleNumber: number) => {
                        flowStateCallback.setManualOverride(true);
                        flowStateCallback.setTitleNumber(newTitleNumber);
                    }}
                />
            );
            break;
    }

    return (
        <>
            {/*
              Persistent ProductOption: kept mounted for the entire flow so its
              <iframe src="/shop"> never reloads. It is only hidden (not
              unmounted) while intermediate screens are showing, so transitioning
              into the ProductOption screen does not refresh the iframe.
            */}
            <Box style={{ display: showProductOption ? "block" : "none" }}>
                <ProductOption
                    activeScreen={flowStateData.currentScreen}
                    handleNext={() => {
                        delayedDispatch(OfflineActiveScreen.AddressInput);
                        flowStateCallback.loadCartFromLocalStorage();
                    }}
                />
            </Box>
            {children}
        </>
    );
}
